import { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardHeader from '@material-ui/core/CardHeader';
import TextField from '@material-ui/core/TextField';
import { RootState } from '../redux/store';
import {
  AlarmDetailFromNotificationMessageDataModel,
  AlarmDetailFromNotificationMessageModel
} from '../@types/alarm-detail';
import { setAlarmDetail } from '../redux/slices/alarmDetail';
import { ALARMANDWARNING } from '../views/BrightIQ/alarm-detail';
import useLocales from '../hooks/useLocales';
import { getImageUrlFromPath } from '../redux/slices/cdn';
import { getHealthStatus } from '../redux/slices/healthCheck';
import { getUserInfoFromToken } from '../redux/slices/authJwt';
import { addFireFighterNotificationData, getAllCompaniesFireFighter } from '../redux/slices/fireFighter';
import { Roles } from '../constants/roles.enum';

type ShowToasterProps = {
  notificationInformation: any;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
};

export default function ShowToaster({
                                      notificationInformation,
                                      isOpen,
                                      setIsOpen
                                    }: ShowToasterProps) {
  const dispatch = useDispatch();
  const { translate } = useLocales();
  const { closeSnackbar, enqueueSnackbar } = useSnackbar();
  const { modulesList } = useSelector((state: RootState) => state.modules);
  const { roles } = useSelector((state: RootState) => state.authJwt);
  const [showPopup, setShowPopup] = useState(false);
  const { cameraBetweenDatesList } = useSelector(
    (state: RootState) => state.camera
  );
  const [img, setImg] = useState('');
  const [parsedData, setParsedData] = useState<AlarmDetailFromNotificationMessageDataModel | null>(null);
  const [currentEventId, setCurrentEventId] = useState<number | null>(null);


  useEffect(() => {
    if (notificationInformation) {
      const parsedData: AlarmDetailFromNotificationMessageDataModel = JSON.parse(notificationInformation?.data?.data ?? '{}') as AlarmDetailFromNotificationMessageDataModel;
      setParsedData(parsedData);

      // Handle event deletion notification to close popup
      if (parsedData.action === 'close_popup' && parsedData.eventId && currentEventId === parsedData.eventId) {
        setIsOpen(false);
        setCurrentEventId(null);
        return;
      }

      const tmp = 'popup' in parsedData && parsedData.popup === true;
      setShowPopup(tmp);

      // Track current event ID when popup opens
      if (tmp && parsedData.eventId) {
        setCurrentEventId(parsedData.eventId);
      }

      if ('sound' in parsedData && parsedData.sound === true && tmp && isOpen) {
        const audio = new Audio('/media/notification.wav');
        audio.play();
      }

      let alertData;

      let cameraDetail;

      if (parsedData && parsedData.moduleId) {
        const module = modulesList.find((item) => item.id === parsedData.moduleId);
        if (module) {
          parsedData.moduleDetail = module;
        }
      }

      if (parsedData && parsedData.alertType) {
        alertData = ALARMANDWARNING.find((item) => item.id === parsedData.alertType);
      }

      if (alertData) {
        parsedData.alertDetail = alertData;
      }

      if (parsedData && parsedData.cameraId) {
        cameraDetail = cameraBetweenDatesList.find(
          (item) => item.id === parsedData.cameraId
        );
      }

      if (cameraDetail) {
        parsedData.cameraDetail = cameraDetail;
      }

      const data: AlarmDetailFromNotificationMessageModel = {
        data: { data: parsedData },
        fcmMessageId: notificationInformation.fcmMessageId,
        from: notificationInformation.from,
        notification: notificationInformation.notification,
        priority: notificationInformation.priority
      };
      dispatch(setAlarmDetail(data));

      if (isOpen) {
        getImageUrlFromPath(parsedData?.image ?? '').then((url) => {
          setImg(url);
        });


        if (roles.includes('ROLE_FIRE_FIGHTER')) {
          addFireFighterNotificationData({
            name: parsedData?.module?.name ?? '',
            image: parsedData?.image ?? '',
            alertType: parsedData?.alertType ?? '',
            cameraId: parsedData?.camera?.id ?? 0,
            companyId: parsedData?.company?.id ?? 0,
            createdAt: parsedData?.createdAt ?? ''
          });
          getAllCompaniesFireFighter();
        }
      }
    }

  }, [isOpen, notificationInformation]);

  const keyRef = useRef<string | number>('');
  const isWarnOpenRef = useRef<boolean>(false);

  useEffect(() => {
    const timer = setInterval(() => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        getUserInfoFromToken(token).then((res) => {
          if (res && res.companyId && res.companyId > 0 && (res.roles.includes(Roles.USER) || res.roles.includes(Roles.CLIENT_ADMIN))) {
            getHealthStatus().then((res) => {
              if (res && !res.isHealthy) {
                if (!isWarnOpenRef.current) {
                  keyRef.current = enqueueSnackbar(
                    translate('global.emergency.server.down'),
                    {
                      variant: 'error',
                      autoHideDuration: null,
                      preventDuplicate: true
                    }
                  );
                  isWarnOpenRef.current = true;
                }
              } else if (isWarnOpenRef.current) {
                closeSnackbar(keyRef.current);
                isWarnOpenRef.current = false;
              }
            });
          }
        });
      } else {
        clearInterval(timer);
      }
    }, 5000);

    return () => clearInterval(timer);
  }, [closeSnackbar, enqueueSnackbar, translate]);

  const onClose = () => {
    setIsOpen(false);
  };

  const getNotificationBody = () => {
    if (
      notificationInformation &&
      notificationInformation.notification &&
      notificationInformation.notification.body
    ) {
      return notificationInformation.notification.body;
    }
    return '';
  };

  return (
    <>
      {
        !parsedData || !showPopup
          ? <></> // Render nothing when popup is false
          : (
            <Dialog maxWidth="xl" open={isOpen}>
              <Box
                sx={{
                  // display: 'flex',
                  // flexDirection: 'column',
                  // width: '100%',
                  // backgroundColor: 'red',
                  // justifyContent: 'center',
                  textAlign: 'center'
                }}
              >
                <DialogTitle sx={{ textAlign: 'center' }}>
                  {getNotificationBody()}
                </DialogTitle>

                <DialogContent
                  sx={{
                    height: '100%',
                    width: '100%',
                    display: 'flex',
                    // justifyContent: 'center',
                    // alignItems: 'center',
                    gap: '10px'
                  }}
                >
                  <Card
                    sx={{
                      width: '70vw' // Up to 70vw for larger screens
                    }}
                  >
                    <img
                      style={{
                        width: '100%',
                        height: '100%',
                        textAlign: 'center',
                        padding: '0.5rem'
                      }}
                      src={img}
                      alt="image"
                    />
                  </Card>

                  <Card
                    sx={{
                      width: '30vw' // Up to 30vw for larger screens
                    }}
                  >
                    <CardHeader title={translate('alarm.detail.information')} />

                    <CardContent
                      sx={{
                        // maxHeight: '300px',
                        padding: '10px',
                        marginTop: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '1rem'
                      }}
                    >
                      <TextField
                        fullWidth
                        size="small"
                        disabled
                        value={parsedData?.module?.name ?? ''}
                        label={translate('alarm.detail.alarm.type')}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        disabled
                        value={parsedData?.camera?.name ?? ''}
                        label={translate('alarm.detail.camera.name')}
                      />

                      <TextField
                        fullWidth
                        size="small"
                        disabled
                        value={parsedData?.company?.name ?? ''}
                        label={translate('alarm.detail.alarm.company')}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        disabled
                        value={
                          parsedData?.createdAt
                            ? new Date(parsedData.createdAt).toLocaleString()
                            : ''
                        }
                        label={translate('alarm.detail.alarm.time')}
                      />
                    </CardContent>
                  </Card>
                </DialogContent>

                <DialogActions sx={{ textAlign: 'center' }}>
                  <Button
                    color="error"
                    onClick={onClose}
                    autoFocus
                    sx={{ textAlign: 'center' }}
                  >
                    {translate('global.close')}
                  </Button>
                </DialogActions>
              </Box>
            </Dialog>
          )
      }
    </>
  );
}
